<template>
  <h2 class="mx-2 mb-6 mt-3 text-xl font-semibold leading-tight text-gray-800">
    {{ place ? t('CreateEditPlace.Edit') : t('CreateEditPlace.Create') }}
  </h2>
  <form
    class="rounded-lg bg-base-100 p-4 shadow-sm"
    @submit.prevent="submit"
    @reset.prevent="form.reset()"
  >
    <NameEditor v-model="form.names" />
    <label class="form-control w-full">
      <div class="label">
        <span class="label-text">{{ t('CreateEditPlace.Geolocation') }}</span>
      </div>
      <Map
        :key="JSON.stringify([form.lat, form.lon])"
        :places="[{
          names: [],
          lat: form.lat,
          lon: form.lon,
        }]"
        class="mb-4"
      />
      <div class="mb-2 flex gap-x-2">
        <input
          v-model.number="form.osm_id"
          type="text"
          :placeholder="t('CreateEditPlace.OsmID')"
          class="input input-bordered w-full"
        >
        <input
          v-model.number="form.lat"
          type="number"
          step="0.000000000001"
          :placeholder="t('CreateEditPlace.Latitude')"
          class="input input-bordered w-full"
          disabled
        >
        <input
          v-model.number="form.lon"
          type="number"
          step="0.000000000001"
          :placeholder="t('CreateEditPlace.Longitude')"
          class="input input-bordered w-full"
          disabled
        >
        <button
          type="button"
          class="btn btn-ghost"
          @click="searchPlaces"
        >
          <WandSparkles />
        </button>
      </div>
    </label>
    <div
      v-for="result in searchResults"
      :key="result.place_id"
      class="flex items-center justify-between rounded-lg p-2 hover:bg-black/5"
    >
      <div class="pl-1">
        <div>
          {{ result.display_name }}
        </div>
        <div class="flex items-center gap-2">
          <span class="badge badge-primary badge-sm font-bold uppercase">
            {{ result.addresstype }}
          </span>
          <span class="text-sm">
            {{ result.lat }}, {{ result.lon }}
          </span>
          <span class="text-sm font-bold">
            {{ result.osm_type.substring(0, 1).toUpperCase() }}{{ result.osm_id }}
          </span>
          <a
            :href="`https://nominatim.openstreetmap.org/ui/details.html?place_id=${result.place_id}`"
            class="link flex items-center gap-1 text-sm"
            target="_blank"
          >
            OpenStreetMap <SquareArrowOutUpRight class="size-3" />
          </a>
        </div>
      </div>
      <button
        type="button"
        class="btn btn-ghost"
        @click="selectResult(result)"
      >
        {{ t('CreateEditPlace.SelectSuggestion') }}
      </button>
    </div>
    <label class="form-control">
      <div class="label">
        <span class="label-text">{{ t('CreateEditPlace.Comment') }}</span>
      </div>
      <textarea
        v-model="form.comment"
        class="textarea textarea-bordered h-24"
        :placeholder="t('CreateEditPlace.Comment')"
      />
    </label>
    <div class="mt-4 flex justify-between">
      <button
        type="reset"
        class="btn btn-outline btn-error w-64"
        :disabled="!form.isDirty"
      >
        {{ t('CreateEditPlace.ClearForm') }}
      </button>
      <button
        type="submit"
        class="btn btn-primary w-64"
        :disabled="!form.isDirty"
      >
        {{ place ? t('CreateEditPlace.Update') : t('CreateEditPlace.Create') }}
      </button>
    </div>
  </form>
</template>
<script lang="ts">
import AppLayout from '@/Layouts/AppLayout.vue';
import NameEditor from '@/Components/NameEditor.vue';
import { HasMaybeSavedNames } from '@/Interfaces/Name';
export default {
  layout: AppLayout,
}
</script>
<script setup lang="ts">
import { Place } from '@/Interfaces/Place';
import { useForm } from '@inertiajs/vue3';
import { SquareArrowOutUpRight, WandSparkles } from 'lucide-vue-next';
import { PropType, ref } from 'vue';
import * as _ from 'lodash-es'
import { MaybeSaved } from '@/Interfaces/Model';
import Map from '@/Components/Map.vue';
import { OSMPlace, useGeocoder } from '@/Composables/geocoder';
import { flatten, uniqBy } from 'lodash-es';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  place: { type: Object as PropType<Place>, default: null },
})

const emit = defineEmits<{
  (event: 'submit'): void
}>()

const { t } = useI18n()

const { search } = useGeocoder()

const searchResults = ref<OSMPlace[]>([])

async function searchPlaces () {
  const results = await Promise.all(form.names.filter(name => !!name.name).map(name => search(name.name)))
  searchResults.value = uniqBy(flatten(results), 'place_id')
}

function selectResult(result: OSMPlace) {
  form.lat = result.lat
  form.lon = result.lon
  form.osm_id = result.osm_type.substring(0, 1).toUpperCase() + result.osm_id
  searchResults.value = []
}

const form = useForm<HasMaybeSavedNames<MaybeSaved<Place>>>(
  props.place
    ? _.cloneDeep(props.place)
    : {
      names: [{ name: '', primary: true }],
      osm_id: undefined,
      lat: undefined,
      lon: undefined,
      comment: '',
    },
)

function submit () {
  if (props.place) {
    form.patch(
      route('places.update', props.place.id),
      {
        onSuccess() {
          emit('submit')
        },
      },
    )
  } else {
    form.post(
      route('places.store'),
      {
        onSuccess() {
          emit('submit')
        },
      },
    )
  }
}
</script>
